/**
 * perfProbe — expose window.__PERF__ with microbenchmarks, frame-time
 * samplers, and a session recorder for diagnosing one-off stalls. Loaded
 * for side effects from main.jsx.
 *
 *   window.__PERF__.sampleFrames(5000)      // 5s rAF stats
 *   window.__PERF__.timeEvaluateBody(2000)  // 2000 evaluateGraph(body) calls
 *   window.__PERF__.timeEvaluateFace(2000)
 *   window.__PERF__.timeFaceClipBlend(2000)
 *   window.__PERF__.snapshotGraphSize()
 *   window.__PERF__.memory()
 *
 *   // Session capture — records long-tasks with attribution + frame deltas
 *   // and any `performance.mark/measure` entries emitted while recording.
 *   // Hot-path code can check `globalThis.__PERF_TRACE__` and emit marks.
 *   const rec = await window.__PERF__.recordSession(async () => {
 *     // do the thing
 *   }, { durationMs: 8000 })
 *   console.log(rec)
 */

import { evaluateGraph } from './GraphEvaluator'
import { graphState } from './graphState'
import { faceGraphState } from './faceGraphState'
import { faceClipPlayer } from './FaceClipPlayer'
import { faceEvalResult } from './faceEvalResult'

function summarize(deltas) {
  if (deltas.length === 0) {
    return { frames: 0, fps: 0, avgMs: 0, p50Ms: 0, p95Ms: 0, p99Ms: 0, maxMs: 0, droppedCount: 0, droppedPct: 0 }
  }
  const sorted = deltas.slice().sort((a, b) => a - b)
  let sum = 0
  for (const d of deltas) sum += d
  const pick = (q) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))]
  // A frame over ~22ms means we dropped the 60Hz budget of 16.67ms
  let dropped = 0
  for (const d of deltas) if (d > 22) dropped++
  return {
    frames:       deltas.length,
    fps:          1000 * deltas.length / sum,
    avgMs:        sum / deltas.length,
    p50Ms:        pick(0.5),
    p95Ms:        pick(0.95),
    p99Ms:        pick(0.99),
    maxMs:        sorted[sorted.length - 1],
    droppedCount: dropped,
    droppedPct:   100 * dropped / deltas.length,
  }
}

function timeLoop(fn, iters) {
  const t0 = performance.now()
  for (let i = 0; i < iters; i++) fn()
  const total = performance.now() - t0
  return { iters, totalMs: total, perCallMs: total / iters }
}

// Serialize a PerformanceLongTaskTiming entry with its attribution — the
// attribution list tells us which script / container caused the stall.
function serializeLongTask(e) {
  const attribution = (e.attribution ?? []).map(a => ({
    name:           a.name,
    entryType:      a.entryType,
    containerType:  a.containerType,
    containerSrc:   a.containerSrc,
    containerId:    a.containerId,
    containerName:  a.containerName,
  }))
  return { name: e.name, duration: e.duration, startTime: e.startTime, attribution }
}

if (typeof window !== 'undefined') {
  let _longTaskObs = null
  const _longTasks = []

  window.__PERF__ = {
    /**
     * Sample per-frame deltas (via requestAnimationFrame) for `durationMs`.
     * Also captures any longtask PerformanceEntry that fires during the window.
     */
    sampleFrames(durationMs = 5000) {
      return new Promise(resolve => {
        const deltas = []
        const sampleLongTasks = []
        let obs = null
        try {
          obs = new PerformanceObserver(list => {
            for (const e of list.getEntries()) sampleLongTasks.push(serializeLongTask(e))
          })
          obs.observe({ entryTypes: ['longtask'] })
        } catch { /* longtask API not available — fine */ }

        let last = performance.now()
        const start = last
        const tick = (now) => {
          deltas.push(now - last)
          last = now
          if (now - start < durationMs) {
            requestAnimationFrame(tick)
          } else {
            if (obs) obs.disconnect()
            const summary = summarize(deltas)
            summary.longTaskCount   = sampleLongTasks.length
            summary.longTaskTotalMs = sampleLongTasks.reduce((a, b) => a + b.duration, 0)
            summary.longTasks       = sampleLongTasks
            resolve(summary)
          }
        }
        requestAnimationFrame(tick)
      })
    },

    /**
     * Record a session while running `action()`. Captures:
     *   - Per-frame deltas (rAF) keyed by wall-clock
     *   - All longtask entries with attribution
     *   - All user measure entries (names starting with "aos.") that code
     *     emits while `__PERF_TRACE__` is truthy
     *
     * Returns a report with the slowest frame + every measure that fell
     * inside that frame's window.
     *
     * @param {() => Promise<any>} action  — awaited while the recorder runs
     * @param {{durationMs?: number}} opts
     */
    async recordSession(action, opts = {}) {
      const { durationMs = 8000 } = opts

      // Clear stale entries so we only see what we record
      performance.clearMarks()
      performance.clearMeasures()

      const longTasks  = []
      const measures   = []
      const frames     = [] // { start, delta }

      let longObs = null
      try {
        longObs = new PerformanceObserver(list => {
          for (const e of list.getEntries()) longTasks.push(serializeLongTask(e))
        })
        longObs.observe({ entryTypes: ['longtask'] })
      } catch { /* ignore */ }

      let measureObs = null
      try {
        measureObs = new PerformanceObserver(list => {
          for (const e of list.getEntries()) {
            if (!e.name.startsWith('aos.')) continue
            measures.push({ name: e.name, startTime: e.startTime, duration: e.duration })
          }
        })
        measureObs.observe({ entryTypes: ['measure'] })
      } catch { /* ignore */ }

      globalThis.__PERF_TRACE__ = true

      const startWall = performance.now()
      let lastFrame = startWall
      let rafId
      const tick = (now) => {
        frames.push({ start: lastFrame, delta: now - lastFrame })
        lastFrame = now
        rafId = requestAnimationFrame(tick)
      }
      rafId = requestAnimationFrame(tick)

      // Run the user action in parallel with a duration cap
      const actionPromise = Promise.resolve().then(action)
      const timer = new Promise(r => setTimeout(r, durationMs))
      await Promise.race([actionPromise, timer])
      // Give a little tail for the last longtask to flush
      await new Promise(r => setTimeout(r, 300))

      cancelAnimationFrame(rafId)
      if (longObs)    longObs.disconnect()
      if (measureObs) measureObs.disconnect()
      globalThis.__PERF_TRACE__ = false

      const deltas = frames.map(f => f.delta)
      const summary = summarize(deltas)

      // Rank the top N slowest frames and collect the aos.* measures that fell
      // inside each. Answers: "which phase is expensive when a spike happens?"
      const topN = frames
        .map((f, i) => ({ idx: i, start: f.start, delta: f.delta }))
        .sort((a, b) => b.delta - a.delta)
        .slice(0, 5)
        .map(f => {
          const end = f.start + f.delta
          const framesMeasures = measures
            .filter(m => m.startTime <= end && (m.startTime + m.duration) >= f.start)
            .sort((a, b) => b.duration - a.duration)
          const framesLongTasks = longTasks.filter(lt =>
            lt.startTime <= end && (lt.startTime + lt.duration) >= f.start,
          )
          return { ...f, measures: framesMeasures, longTasks: framesLongTasks }
        })

      return {
        summary,
        topSlowFrames: topN,
        longTasks,
        longTaskCount: longTasks.length,
        measureCount: measures.length,
      }
    },

    timeEvaluateBody(iters = 2000) {
      return timeLoop(() => evaluateGraph(graphState), iters)
    },
    timeEvaluateFace(iters = 2000) {
      return timeLoop(() => evaluateGraph(faceGraphState), iters)
    },
    timeFaceClipBlend(iters = 2000) {
      const weights = faceEvalResult.weights ?? {}
      return timeLoop(() => faceClipPlayer.getBlendedWeights(weights), iters)
    },

    /** How large are the graphs we're evaluating — helps correlate cost to content. */
    snapshotGraphSize() {
      const smInfo = (nodes) => {
        const sms = nodes.filter(n => n.type === 'stateMachine')
        return sms.map(n => ({
          id: n.id,
          states: (n.data?.innerNodes ?? []).filter(i => i.type === 'state').length,
          transitions: (n.data?.innerEdges ?? []).length,
        }))
      }
      return {
        body: {
          nodes: graphState.nodes.length,
          edges: graphState.edges.length,
          stateMachines: smInfo(graphState.nodes),
        },
        face: {
          nodes: faceGraphState.nodes.length,
          edges: faceGraphState.edges.length,
          stateMachines: smInfo(faceGraphState.nodes),
        },
        faceClipsLoaded: Object.keys(faceClipPlayer._clips ?? {}).length,
        faceClipsActive: Object.keys(faceClipPlayer._active ?? {}).length,
      }
    },

    /** Start/stop a global longtask log that persists across calls. */
    startLongTaskLog() {
      if (_longTaskObs) return
      _longTasks.length = 0
      try {
        _longTaskObs = new PerformanceObserver(list => {
          for (const e of list.getEntries()) _longTasks.push(serializeLongTask(e))
        })
        _longTaskObs.observe({ entryTypes: ['longtask'] })
      } catch { /* unsupported */ }
    },
    stopLongTaskLog() {
      if (_longTaskObs) { _longTaskObs.disconnect(); _longTaskObs = null }
      return _longTasks.slice()
    },

    /** Rough JS heap size if available (Chrome only). */
    memory() {
      const m = performance.memory
      if (!m) return null
      return {
        usedJSHeapMB:  +(m.usedJSHeapSize / 1e6).toFixed(1),
        totalJSHeapMB: +(m.totalJSHeapSize / 1e6).toFixed(1),
        limitMB:       +(m.jsHeapSizeLimit / 1e6).toFixed(1),
      }
    },
  }
}

/**
 * Hot-path helper — wrap a sync block with a named measure that only
 * records when __PERF_TRACE__ is on. The early-return when tracing is off
 * is a single property read, so this is safe to leave in production code.
 */
export function tracePhase(name, fn) {
  if (!globalThis.__PERF_TRACE__) return fn()
  const startMark = `aos.${name}.start`
  performance.mark(startMark)
  try {
    return fn()
  } finally {
    performance.measure(`aos.${name}`, startMark)
  }
}
